import { Injectable } from '@angular/core';
import { CalculationModelsState, CalculationState } from '../../calculation/calculation.state';
import { setCalculation } from '../../calculation/calculation.actions';
import { CalculationTypeEnum } from '../../../api/models/calculation-type-enum';
import { OneSlopeMeasurementDto } from '../../../api/models/one-slope-measurement-dto';
import { TwoSlopeMeasurementDto } from '../../../api/models/two-slope-measurement-dto';
import { FourSlopeMeasurementDto } from '../../../api/models/four-slope-measurement-dto';
import { CustomMeasurementDto } from '../../../api/models/custom-measurement-dto';
import { CustomShapesMeasurementDto } from '../../../api/models/custom-shapes-measurement-dto';
import { ShapeDto } from '../../../api/models/shape-dto';
import { environment } from '../../../environments/environment';
import { CalculationsService } from '../../../api/services/calculations.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.state';
import { CalculationDto } from '../../../api/models/calculation-dto';
import { lastValueFrom } from 'rxjs';
import { FenceSlatMeasurementsDto } from '../../../api/models/fence-slat-measurements-dto';

@Injectable()
export class RoofCalculationsService {
  constructor(private readonly calculationsService: CalculationsService, private readonly store: Store<AppState>) {}
  async calculateRoof(
    { measurements, accessories, roofType, annexes, rainSystem }: CalculationState,
    models: CalculationModelsState,
  ): Promise<CalculationDto> {
    let calculation: CalculationDto;
    const body = {
      roofColorId: models.color.id,
      hasDripStop: models.hasDripStop,
      accessories,
      annexes,
    };
    switch (roofType) {
      case CalculationTypeEnum.OneSlope:
        calculation = (await lastValueFrom(
          this.calculationsService.calculationsControllerOneSlopeCalculator({
            body: {
              ...body,
              measurements: { ...(measurements as OneSlopeMeasurementDto), rainSystem },
            },
          }),
        )) as CalculationDto;
        break;

      case CalculationTypeEnum.TwoSlopes:
        calculation = (await this.calculationsService
          .calculationsControllerTwoSlopesCalculator({
            body: {
              ...body,
              measurements: { ...(measurements as TwoSlopeMeasurementDto), rainSystem },
            },
          })
          .toPromise()) as CalculationDto;
        break;
      case CalculationTypeEnum.FourSlopes:
        calculation = (await this.calculationsService
          .calculationsControllerFourSlopes({
            body: {
              ...body,
              measurements: { ...(measurements as FourSlopeMeasurementDto), rainSystem },
            },
          })
          .toPromise()) as CalculationDto;
        break;
      case CalculationTypeEnum.Custom:
        const customMeasurements = { ...measurements } as CustomMeasurementDto;
        customMeasurements.tilesByHeight = customMeasurements.tilesByHeight?.filter(
          (tile) => tile.count > 0 && tile.height >= 0.5,
        );
        customMeasurements.tilesByHeight = customMeasurements.tilesByHeight?.length
          ? customMeasurements.tilesByHeight
          : undefined;
        calculation = (await this.calculationsService
          .calculationsControllerCustom({
            body: {
              ...body,
              measurements: { ...customMeasurements, rainSystem },
            },
          })
          .toPromise()) as CalculationDto;
        break;
      case CalculationTypeEnum.CustomShape:
        const apiMeasurements: CustomShapesMeasurementDto = {
          ...(measurements as CustomShapesMeasurementDto),
          rainSystem,
        };
        if (roofType === CalculationTypeEnum.CustomShape) {
          apiMeasurements.shapes = apiMeasurements.shapes.filter(({ points }: ShapeDto) => points.length > 0);
        }
        calculation = (await this.calculationsService
          .calculationsControllerCustomShape({
            body: {
              ...body,
              measurements: apiMeasurements,
            },
          })
          .toPromise()) as CalculationDto;
        break;
      case CalculationTypeEnum.FenceSlat:
        calculation = (await lastValueFrom(
          this.calculationsService.calculationsControllerFenceSlatCalculator({
            body: {
              ...body,
              measurements: { ...(measurements as FenceSlatMeasurementsDto) },
            },
          }),
        )) as CalculationDto;
        break;
      default:
        throw new Error('Unknown calculation type');
    }
    this.store.dispatch(setCalculation({ calculation }));
    if (!!environment.gtm) {
      (<any>window).gtag('event', 'conversion', {
        send_to: 'AW-11371316789/cetCCLagmO4YELWMoq4q',
        transaction_id: calculation.id,
      });
    }
    return calculation;
  }
}
